import * as React from "react";
import { Button, Checkbox, Input, Toast, ToastTitle, Toaster, useId, useToastController } from "@fluentui/react-components";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@fluentui/react-table";
import { Add24Regular, CheckmarkCircle24Regular, Delete24Regular, Dismiss24Regular, Edit24Regular, Location24Regular } from "@fluentui/react-icons";
import { apiUrl } from "../../utils/api";
import { Address } from "./types";

type AddressManagementSectionProps = {
    contactId: number;
    addresses: Address[];
    title?: string;
};

export const AddressManagementSection: React.FC<AddressManagementSectionProps> = ({
    contactId,
    addresses,
    title = "Anschriften",
}) => {
    const [addressList, setAddressList] = React.useState<Address[]>(addresses);
    const [selectedAddress, setSelectedAddress] = React.useState<Address | null>(null);

    const [addrDesc, setAddrDesc] = React.useState("");
    const [addrStreet, setAddrStreet] = React.useState("");
    const [addrCity, setAddrCity] = React.useState("");
    const [addrPostal, setAddrPostal] = React.useState("");
    const [addrOrtsteil, setAddrOrtsteil] = React.useState("");
    const [addrCountry, setAddrCountry] = React.useState("");
    const [addrDefault, setAddrDefault] = React.useState(false);

    const toasterId = useId("address-toaster");
    const { dispatchToast } = useToastController(toasterId);

    React.useEffect(() => {
        setAddressList(addresses);
    }, [addresses]);

    React.useEffect(() => {
        if (selectedAddress) {
            setAddrDesc(selectedAddress.addressDescription || "");
            setAddrStreet(selectedAddress.street || "");
            setAddrCity(selectedAddress.city || "");
            setAddrPostal(selectedAddress.postalCode || "");
            setAddrOrtsteil(selectedAddress.ortsteil || "");
            setAddrCountry(selectedAddress.country || "");
            setAddrDefault(selectedAddress.isDefault);
            return;
        }

        setAddrDesc("");
        setAddrStreet("");
        setAddrCity("");
        setAddrPostal("");
        setAddrOrtsteil("");
        setAddrCountry("");
        setAddrDefault(false);
    }, [selectedAddress]);

    const handleNewAddress = () => {
        setSelectedAddress({
            id: Date.now(),
            addressDescription: "",
            street: "",
            city: "",
            postalCode: "",
            ortsteil: "",
            country: "",
            isDefault: false,
        });
    };

    const handleSaveAddress = async () => {
        if (!selectedAddress) {
            return;
        }

        try {
            const payload = {
                addressDescription: addrDesc,
                street: addrStreet,
                city: addrCity,
                postalCode: addrPostal,
                ortsteil: addrOrtsteil,
                country: addrCountry,
                isDefault: addrDefault,
            };

            const isNew = !addresses.some((address) => address.id === selectedAddress.id);

            if (isNew) {
                const response = await fetch(apiUrl(`contact/${contactId}/address`), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const newAddress = await response.json();
                setAddressList((prev) => [...prev, newAddress]);
            } else {
                const response = await fetch(apiUrl(`contact/${contactId}/address/${selectedAddress.id}`), {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const updatedAddress = await response.json();
                setAddressList((prev) => prev.map((address) => (address.id === selectedAddress.id ? updatedAddress : address)));
            }

            setSelectedAddress(null);
            dispatchToast(<Toast><ToastTitle>Adresse gespeichert</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error saving address:", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Speichern</ToastTitle></Toast>, { intent: "error" });
        }
    };

    const handleDeleteAddress = async (id: number) => {
        try {
            await fetch(apiUrl(`contact/${contactId}/address/${id}`), { method: "DELETE" });
            setAddressList((prev) => prev.filter((address) => address.id !== id));
            if (selectedAddress?.id === id) {
                setSelectedAddress(null);
            }
            dispatchToast(<Toast><ToastTitle>Adresse gelöscht</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error deleting address:", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Löschen</ToastTitle></Toast>, { intent: "error" });
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Toaster toasterId={toasterId} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <h3>{title}</h3>
                <Button icon={<Add24Regular />} onClick={handleNewAddress}>Neu</Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>Bezeichnung</TableHeaderCell>
                        <TableHeaderCell>Straße</TableHeaderCell>
                        <TableHeaderCell>Ort</TableHeaderCell>
                        <TableHeaderCell>Ortsteil</TableHeaderCell>
                        <TableHeaderCell style={{ width: "80px" }}>PLZ</TableHeaderCell>
                        <TableHeaderCell>Risikoort</TableHeaderCell>
                        <TableHeaderCell style={{ width: "140px" }}>Aktion</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[...addressList]
                        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                        .map((address) => (
                            <TableRow
                                key={address.id}
                                onClick={() => setSelectedAddress(address)}
                                style={{ cursor: "pointer", background: selectedAddress?.id === address.id ? "#f0f6ff" : undefined }}
                            >
                                <TableCell>{address.addressDescription}</TableCell>
                                <TableCell>{address.street}</TableCell>
                                <TableCell>{address.city}</TableCell>
                                <TableCell>{address.ortsteil}</TableCell>
                                <TableCell>{address.postalCode}</TableCell>
                                <TableCell>{address.riskType && <CheckmarkCircle24Regular />}</TableCell>
                                <TableCell>
                                    <Location24Regular
                                        style={{ cursor: "pointer", marginRight: 8 }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            window.open(`https://maps.google.com/?q=${address.street},${address.postalCode} ${address.city}`, "_blank");
                                        }}
                                    />
                                    <Edit24Regular style={{ cursor: "pointer", marginRight: 8 }} onClick={(event) => { event.stopPropagation(); setSelectedAddress(address); }} />
                                    <Delete24Regular style={{ cursor: "pointer" }} onClick={(event) => { event.stopPropagation(); handleDeleteAddress(address.id); }} />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>

            {selectedAddress && (
                <div style={{ marginTop: 20, padding: 20, border: "1px solid #ccc", borderRadius: 4, position: "relative" }}>
                    <Dismiss24Regular style={{ position: "absolute", top: 10, right: 10, cursor: "pointer" }} onClick={() => setSelectedAddress(null)} />
                    <h4>Adresse bearbeiten</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Input value={addrDesc} onChange={(_, data) => setAddrDesc(data.value)} placeholder="Bezeichnung" />
                        <Input value={addrStreet} onChange={(_, data) => setAddrStreet(data.value)} placeholder="Straße" />
                        <Input value={addrCity} onChange={(_, data) => setAddrCity(data.value)} placeholder="Ort" />
                        <Input value={addrPostal} onChange={(_, data) => setAddrPostal(data.value)} placeholder="PLZ" />
                        <Input value={addrOrtsteil} onChange={(_, data) => setAddrOrtsteil(data.value)} placeholder="Ortsteil" />
                        <Input value={addrCountry} onChange={(_, data) => setAddrCountry(data.value)} placeholder="Land" />
                    </div>
                    <Checkbox checked={addrDefault} onChange={(_, data) => setAddrDefault(data.checked === true)} label="Als Standard festlegen" style={{ marginTop: 10 }} />
                    <Button appearance="primary" onClick={handleSaveAddress} style={{ marginTop: 10 }}>Speichern</Button>
                </div>
            )}
        </div>
    );
};
